import React from "react";
import styled, { css } from "styled-components";
import { useTranslate } from "@refinedev/core";
import { Reveal } from "./styles";
import { AnimatedSentence } from "./AnimatedSentence";
import { EncryptedNote } from "./EncryptedNote";
import imgCtaDocuments from "@/assets/images/onboarding/cta-documents.png";
import imgCtaPrompt from "@/assets/images/onboarding/cta-prompt.png";
import imgCtaPhoto from "@/assets/images/onboarding/cta-photo.png";
import imgCtaUrl from "@/assets/images/onboarding/cta-url.png";
import { radius } from "@libertify/ui";

// ─── Styled components ────────────────────────────────────────────────────────

const Content = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (width <= 768px) {
    padding: 0 4px;
  }
`;

const HeroTitle = styled.h1`
  font-family: "Poppins", sans-serif;
  font-size: 48px;
  font-weight: 300;
  color: #090e19;
  text-align: center;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin: 0 0 12px;

  @media (width <= 768px) {
    font-size: 34px;
  }

  @media (width <= 480px) {
    font-size: 26px;
    letter-spacing: -0.02em;
  }
`;

const GradientWord = styled.span`
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  background: linear-gradient(to bottom right, #00b5a9, #540084);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 15px;
  color: #6b7280;
  text-align: center;
  margin: 0;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  margin-top: 32px;

  @media (width <= 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $disabled?: boolean }>`
  background: white;
  border-radius: ${radius.box}px;
  border: 1px solid #c9cfcf;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0 0 24px;
  position: relative;
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  filter: ${(p) => (p.$disabled ? "grayscale(1)" : "grayscale(0)")};
  transition:
    border-color 0.15s,
    box-shadow 0.15s;

  ${(p) =>
    !p.$disabled &&
    css`
      &:hover {
        border: 1px solid #14b8a6;
        box-shadow: 0 0 25px -15px rgba(20, 184, 166, 0.5);
      }
    `}

  @media (width <= 480px) {
    padding: 16px 0 0 16px;
  }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 16px;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`;

const CardTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 28px;
  font-weight: 600;
  color: #090e19;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin: 0;

  @media (width <= 480px) {
    font-size: 22px;
  }
`;

const ComingSoonBadge = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: white;
  background: #ec4899;
  border-radius: ${radius.pill}px;
  padding: 4px 12px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  position: absolute;
  top: 32px;
  right: 32px;
`;

const CardDesc = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const CardIllustration = styled.img`
  width: 180px;
  height: auto;
  flex-shrink: 0;
  object-fit: contain;
  border-radius: 0 0 ${radius.box}px;

  @media (width <= 768px) {
    width: 130px;
  }

  @media (width <= 480px) {
    width: 100px;
  }
`;

// ─── Card component ─────────────────────────────────────────────────────────

type OnboardingCardProps = {
  title: string;
  description: string;
  image: string;
  onClick?: () => void;
  comingSoon?: boolean;
};

const OnboardingCard: React.FC<OnboardingCardProps> = ({
  title,
  description,
  image,
  onClick,
  comingSoon,
}) => {
  const t = useTranslate();
  return (
    <Card $disabled={comingSoon} onClick={comingSoon ? undefined : onClick}>
      {comingSoon && (
        <ComingSoonBadge>
          {t("pages.home.onboarding.comingSoonBadge")}
        </ComingSoonBadge>
      )}
      <CardBody>
        <CardHeaderRow>
          <CardTitle>{title}</CardTitle>
        </CardHeaderRow>
        <CardDesc>{description}</CardDesc>
      </CardBody>
      <CardIllustration src={image} alt="" />
    </Card>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

export type OnboardingHomeViewProps = {
  revealed: boolean;
  animate: boolean;
  onSelectDocument: () => void;
  onSelectPrompt: () => void;
};

export const OnboardingHomeView: React.FC<OnboardingHomeViewProps> = ({
  revealed,
  animate,
  onSelectDocument,
  onSelectPrompt,
}) => {
  const t = useTranslate();
  return (
    <Content>
      <HeroTitle>
        <AnimatedSentence
          sentence={t("pages.home.onboarding.heroTitle")}
          revealed={revealed}
          animate={animate}
          baseDelay={0}
          stepMs={120}
          components={{ gradient: <GradientWord /> }}
        />
      </HeroTitle>

      <HeroSubtitle>
        <AnimatedSentence
          sentence={t("pages.home.onboarding.heroSubtitle")}
          revealed={revealed}
          animate={animate}
          baseDelay={360}
          stepMs={80}
        />
      </HeroSubtitle>

      <CardGrid>
        <Reveal $on={revealed} $anim={animate} $d={860}>
          <OnboardingCard
            title={t("pages.home.onboarding.cardDocumentTitle")}
            description={t("pages.home.onboarding.cardDocumentDesc")}
            image={imgCtaDocuments}
            onClick={onSelectDocument}
          />
        </Reveal>

        <Reveal $on={revealed} $anim={animate} $d={1080}>
          <OnboardingCard
            title={t("pages.home.onboarding.cardPromptTitle")}
            description={t("pages.home.onboarding.cardPromptDesc")}
            image={imgCtaPrompt}
            onClick={onSelectPrompt}
          />
        </Reveal>

        <Reveal $on={revealed} $anim={animate} $d={1300}>
          <OnboardingCard
            title={t("pages.home.onboarding.cardPhotoTitle")}
            description={t("pages.home.onboarding.cardPhotoDesc")}
            image={imgCtaPhoto}
            comingSoon
          />
        </Reveal>

        <Reveal $on={revealed} $anim={animate} $d={1520}>
          <OnboardingCard
            title={t("pages.home.onboarding.cardUrlTitle")}
            description={t("pages.home.onboarding.cardUrlDesc")}
            image={imgCtaUrl}
            comingSoon
          />
        </Reveal>
      </CardGrid>

      <Reveal $on={revealed} $anim={animate} $d={1680}>
        <EncryptedNote />
      </Reveal>
    </Content>
  );
};
